/*
 * Live Calendar — full-month grid styled to match the v1 design Sarah
 * approved: a cream card on the theme bg, big orange "Month YYYY"
 * heading top-left, square ‹/› chevron buttons top-right, dotted
 * orange divider under the weekday strip, and large rounded day cells
 * with the day number top-right and color-coded holiday/event pills
 * stacked at the bottom.
 */

.calendar {
  position: relative;
  z-index: 1;
  /* Edge + chrome clearance is now handled by the live stage safe-area
   * (--token-safe-*), so the calendar just fills its safe box. The old
   * hand-tuned asymmetric padding (to dodge the mascot / chevrons /
   * nav) is gone — it double-inset and threw off centering. */
  font-family: var(--token-font-ui);
  color: var(--token-text-primary);
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.empty {
  position: relative;
  z-index: 1;
  padding: var(--token-space-2xl);
  font-family: var(--token-font-ui);
}

.card {
  flex: 1;
  /* Frosted glass — the theme bg shows through behind the calendar so
   * the card reads as part of the room rather than a solid beige
   * rectangle pasted on top. Cream-tinted white keeps day cells
   * readable on top. Bumped ~25 points more solid (0.45 → 0.70) so
   * day numbers + pills stay legible against busier theme bgs. */
  background: rgba(255, 250, 240, 0.7);
  backdrop-filter: blur(22px) saturate(160%);
  -webkit-backdrop-filter: blur(22px) saturate(160%);
  border: 1.5px solid var(--token-accent);
  border-radius: 24px;
  padding: 22px clamp(20px, 2vw, 36px) 16px;
  box-shadow:
    0 30px 60px -20px rgba(8, 18, 12, 0.45),
    inset 0 1px 0 rgba(255, 255, 255, 0.45);
  display: flex;
  flex-direction: column;
  min-height: 0;
  /* Fill the safe box up to a generous cap so the calendar uses an
   * ultra-wide display instead of pillarboxing (was 1800px, which left
   * big side margins on 21:9 + 4K), while staying centered. */
  max-width: min(2800px, 100%);
  margin: 0 auto;
  width: 100%;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 4px;
}

.monthTitle {
  margin: 0;
  font-family: var(--token-font-display);
  font-size: clamp(36px, 3.4vw, 56px);
  font-weight: 700;
  color: #000;
  letter-spacing: 0.5px;
  line-height: 1;
}

/* Wraps the optional "School calendar" pill + the prev/next nav so
 * they sit side-by-side at the right edge of the header. */
.headerActions {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* "School calendar" pill — surfaces the schoolCalUrl saved on the
 * Calendar settings page. Reads as a tappable affordance for the
 * teacher to open the district calendar PDF mid-meeting. Sized to
 * sit comfortably next to the chevron nav without competing with
 * the month title. */
.schoolCalLink {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 999px;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  background: color-mix(in srgb, var(--token-accent) 47%, transparent);
  color: #000;
  font-family: var(--token-font-ui);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-decoration: none;
  max-width: clamp(180px, 22vw, 320px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 120ms ease;
}

.schoolCalLink:hover {
  background: color-mix(in srgb, var(--token-accent) 60%, transparent);
}

.monthNav {
  display: flex;
  gap: 6px;
}

.monthNav button {
  width: 44px;
  height: 36px;
  padding: 0;
  border-radius: 10px;
  border: 1.5px solid rgba(255, 255, 255, 0.6);
  background: color-mix(in srgb, var(--token-accent) 47%, transparent);
  color: #000;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
}

.monthNav button:hover {
  background: color-mix(in srgb, var(--token-accent) 60%, transparent);
}

.weekHeader {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 8px;
  margin-top: 6px;
  padding-bottom: 8px;
  border-bottom: 2px dotted var(--token-accent);
  font-family: var(--token-font-ui);
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: #000;
}

.weekHeaderCell {
  text-align: center;
  padding: 4px;
}

.grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: 1fr;
  gap: 8px;
  margin-top: 10px;
  flex: 1;
  min-height: 0;
}
